import { BaseService, BaseServiceConstructor } from 'api/services/base.service';
import { AxiosRequestConfig } from 'axios';
import { plainToInstance } from 'class-transformer';
import { Inspection, InspectionDetails, Property } from 'types/models';
import {
  InspectionLocation,
  InspectionMethod,
  InspectionStatusOption,
  InspectionType,
} from 'types/models/inspections';
import InspectionStatus from 'types/models/inventory/InspectionStatus';
import { PropertyDetails } from 'types/models/inventory/PropertyDetails';
import { ApproveInspection } from 'types/requests/inspections/ApproveInspection';
import { ConnectorInspection } from 'types/requests/inspections/ConnectorInspection';
import { OnPremisePlumbingInspection } from 'types/requests/inspections/OnPremisePlumbingInspection';
import {
  RejectConnectorInspection,
  RejectOnPremisePlumbingInspection,
  RejectServiceLineInspection,
} from 'types/requests/inspections/RejectInspection';
import { ServiceLineInspection } from 'types/requests/inspections/ServiceLineInspection';
import { PaginatedResponse } from 'types/PaginatedResponse';

export class InspectionService extends BaseService {
  constructor({ serviceUrls, token, tenantName }: BaseServiceConstructor) {
    super({ serviceUrls, token, tenantName });
  }

  async getInspectionMethods(): Promise<InspectionMethod[]> {
    const { data } = await this.httpClient.get<InspectionMethod[]>(`/Dropdowns/InspectionMethods`);
    return data;
  }

  async getInspectionTypes(): Promise<InspectionType[]> {
    const { data } = await this.httpClient.get<InspectionType[]>(`/Dropdowns/InspectionTypes`);
    return data;
  }

  async getInspectionLocations(): Promise<InspectionLocation[]> {
    const { data } = await this.httpClient.get<InspectionLocation[]>(
      `/Dropdowns/InspectionLocations`
    );
    return data;
  }

  async uploadInspectionsFromTemplate(multiPartForm: FormData): Promise<string> {
    const { data } = await this.httpClient.post<string>(`/Inventory/Inspections`, multiPartForm, {
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        Accept: 'application/octet-stream',
      },
    });
    return data;
  }

  async getInspectionStatusOptions(): Promise<InspectionStatusOption[]> {
    const { data } = await this.httpClient.get<InspectionStatusOption[]>(
      `/Dropdowns/InspectionStatuses`,
      {
        withCredentials: false,
      }
    );
    return data.filter((status) => status.Code !== 'Deleted');
  }

  async submitConnectorInspection(
    propertyId: Property['id'],
    requestBody: ConnectorInspection
  ): Promise<PropertyDetails> {
    const { data } = await this.publicHttpClient.post<PropertyDetails>(
      `/properties/${propertyId}/inspections/connector`,
      requestBody,
      {
        withCredentials: false,
      }
    );
    return plainToInstance(PropertyDetails, data);
  }

  async submitOnPremisePlumbingInspection(
    propertyId: Property['id'],
    requestBody: OnPremisePlumbingInspection
  ): Promise<PropertyDetails> {
    const { data } = await this.publicHttpClient.post<PropertyDetails>(
      `/properties/${propertyId}/inspections/premisePlumbing`,
      requestBody,
      {
        withCredentials: false,
      }
    );
    return plainToInstance(PropertyDetails, data);
  }

  async submitServiceLineInspection(
    propertyId: Property['id'],
    requestBody: ServiceLineInspection
  ): Promise<PropertyDetails> {
    const { data } = await this.publicHttpClient.post<PropertyDetails>(
      `/properties/${propertyId}/inspections`,
      requestBody,
      {
        withCredentials: false,
      }
    );
    return plainToInstance(PropertyDetails, data);
  }

  async approveInspection(inspectionId: Inspection['id'], body: ApproveInspection) {
    const { data, warnings } = await this.publicHttpClient.put<{
      updatedProperty: PropertyDetails;
    }>(`/inspections/${inspectionId}/approve`, body, {
      withCredentials: false,
    });
    return {
      updatedProperty: plainToInstance(PropertyDetails, data),
      warnings: warnings,
    };
  }

  async rejectServiceLineInspection(
    inspectionId: Inspection['id'],
    body: RejectServiceLineInspection
  ) {
    const { data: updatedProperty } = await this.publicHttpClient.put<PropertyDetails>(
      `/inspections/${inspectionId}/serviceLine/reject`,
      body,
      {
        withCredentials: false,
      }
    );
    return plainToInstance(PropertyDetails, updatedProperty);
  }

  async rejectConnectorInspection(inspectionId: Inspection['id'], body: RejectConnectorInspection) {
    const { data: updatedProperty } = await this.publicHttpClient.put<PropertyDetails>(
      `/inspections/${inspectionId}/connector/reject`,
      body,
      {
        withCredentials: false,
      }
    );
    return plainToInstance(PropertyDetails, updatedProperty);
  }

  async rejectOnPremisePlumbingInspection(
    inspectionId: Inspection['id'],
    body: RejectOnPremisePlumbingInspection
  ) {
    const { data: updatedProperty } = await this.publicHttpClient.put<PropertyDetails>(
      `/inspections/${inspectionId}/premise/reject`,
      body,
      {
        withCredentials: false,
      }
    );
    return plainToInstance(PropertyDetails, updatedProperty);
  }

  async deleteInspection(inspectionId: Inspection['id']) {
    const { data: updatedProperty } = await this.publicHttpClient.delete<PropertyDetails>(
      `/inspections/${inspectionId}`,
      undefined,
      {
        withCredentials: false,
      }
    );
    return plainToInstance(PropertyDetails, updatedProperty);
  }

  async getInspectionStatuses(
    propertyIds: Property['id'][],
    options: AxiosRequestConfig = {}
  ): Promise<InspectionStatus[]> {
    const { data } = await this.publicHttpClient.post<InspectionStatus[]>(
      `/properties/inspectionStatus`,
      {
        propertyIds,
      },
      {
        withCredentials: false,
        ...options,
      }
    );
    return data;
  }

  async getPaginatedInspections(
    page,
    limit = 25,
    sort = [],
    filters = {}
  ): Promise<PaginatedResponse<InspectionDetails>> {
    let params: any = {
      ...filters,
      'page[number]': page,
      'page[size]': limit,
    };

    if (sort.length) {
      params = {
        ...params,
        sort: sort.join(),
      };
    }

    const resp = await this.publicHttpClient.get<InspectionDetails[]>(`/inspections`, {
      params,
      paramsSerializer: { indexes: null },
      withCredentials: false,
    });

    if (resp.status === 404) {
      return {
        data: [],
        pagination: {
          prev: null,
          next: null,
          last: null,
          count: 0,
          limit: null,
        },
      };
    }
    return resp;
  }
}
